import { z } from 'zod';

export const CONVERSATION_KINDS = ['sales', 'support'] as const;

export const zConversationKind = z.literal(CONVERSATION_KINDS);
export type ConversationKind = z.output<typeof zConversationKind>;

const baseConversationSchema = z.object({
  /** Customer-visible Agent SDK thread. Contains the full transcript. */
  agentThreadId: z.string(),
  /**
   * Inbox flag: an operator hasn't yet opened this conversation since its last
   * noteworthy event. Flipped to `true` by `sendUserMessage` (new customer
   * message) and by the assistant reply path; cleared by `markConversationSeen`
   * when an admin opens the conversation. Drives the unseen-activity dot in the
   * admin inbox list.
   */
  hasUnseenActivity: z.boolean().default(false),
  /** True while the agent is generating a reply for the latest customer message. */
  isAgentGeneratingResponse: z.boolean().default(false),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});

const salesConversationSchema = baseConversationSchema.extend({
  kind: z.literal('sales'),
  /**
   * Per-visitor secret stored in the chat widget's localStorage. Functions as a
   * bearer token for unauthenticated sales conversations: required by every
   * visitor query/mutation (see `assertVisitorAccess`). Also the stable key
   * pointing at the visitor's single, permanent conversation.
   */
  visitorIdentifier: z.string(),
  restaurantUserId: z.null(),
});

const supportConversationSchema = baseConversationSchema.extend({
  kind: z.literal('support'),
  visitorIdentifier: z.null(),
  restaurantUserId: z.string(),
});

const salesConversationDbSchema = salesConversationSchema.omit({
  _creationTime: true,
  _id: true,
});

const supportConversationDbSchema = supportConversationSchema.omit({
  _creationTime: true,
  _id: true,
});

export const conversationSchema = z.discriminatedUnion('kind', [
  salesConversationSchema,
  supportConversationSchema,
]);
export type Conversation = z.output<typeof conversationSchema>;

export const conversationStrictSchema = conversationSchema;

export const conversationDbSchema = z.discriminatedUnion('kind', [
  salesConversationDbSchema,
  supportConversationDbSchema,
]);

export const conversationCvxSchema = z.discriminatedUnion('kind', [
  salesConversationSchema.extend({ _creationTime: z.number(), _id: z.string() }),
  supportConversationSchema.extend({ _creationTime: z.number(), _id: z.string() }),
]);
export type ConversationCvx = z.output<typeof conversationCvxSchema>;
